import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

// --- DATA ---
// --- DATA ---
const DEFAULT_HERO_IMAGE = 'https://skyphaseobeng.s3.eu-north-1.amazonaws.com/casual2.JPG';

const DEFAULT_COLLECTIONS = [
  {
    id: 'nuptials',
    title: 'BRIDALS',
    subtitle: 'ELEGANCE & GRACE',
    img: 'https://skyphaseobeng.s3.eu-north-1.amazonaws.com/bridal4.jpg',
    type: 'portrait',
    path_name: '/bookings',
  },
  {
    id: 'exclusives',
    title: 'EXCLUSIVES',
    subtitle: 'ONE OF A KIND',
    img: 'https://skyphaseobeng.s3.eu-north-1.amazonaws.com/1emba.JPG',
    type: 'portrait',
    path_name: '/bookings',
  },
  {
    id: 'kente',
    title: 'KENTE',
    subtitle: 'TRADITION MEETS MODERN',
    img: 'https://skyphaseobeng.s3.eu-north-1.amazonaws.com/kente2.JPG',
    type: 'portrait',
    path_name: '/bookings',
  },
  {
    id: 'casuals',
    title: 'CASUALS',
    subtitle: 'EVERYDAY LUXURY',
    img: 'https://skyphaseobeng.s3.eu-north-1.amazonaws.com/casual3.JPG',
    type: 'portrait',
    path_name: '/bookings',
  },
  {
    id: 'bridal-2',
    title: 'THE WEDDING EDIT',
    subtitle: 'FOR THE SPECIAL DAY',
    img: 'https://skyphaseobeng.s3.eu-north-1.amazonaws.com/bridal1.JPG',
    type: 'landscape',
    path_name: '/bookings',
  },
];

// --- COMPONENTS ---

function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);

    // Load user
    const userData = localStorage.getItem('emba_user');
    if (userData) {
      try {
        const parsed = JSON.parse(userData);
        setUser(parsed.user || parsed);
      } catch (e) {
        console.error("Failed to parse user", e);
      }
    }

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const displayName = user ? `${user.first_name} ${user.last_name}` : 'ACCOUNT';

  return (
    <>
      <motion.header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? 'bg-white/90 backdrop-blur-md shadow-sm py-3' : 'bg-transparent py-6'
          }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <div className="w-full px-6 md:px-12 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Logo" className="h-8 w-8 object-contain" />
            <a href="/" className={`text-sm tracking-[0.3em] font-bold ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
              EMBA BY EMEFA
            </a>
          </div>

          {/* Desktop Nav */}
          <nav className={`hidden md:flex gap-10 items-center text-xs tracking-widest font-medium ${isScrolled ? 'text-gray-800' : 'text-white'}`}>
            <a href="/" className="hover:opacity-60 transition-opacity">HOME</a>
            <a href="/bookings" className="hover:opacity-60 transition-opacity">BOOKINGS</a>
            <a href="/store" className="hover:opacity-80 transition-opacity bg-black text-white px-4 py-2 font-bold shadow-md transform hover:scale-105 rounded-sm">SHOP</a>
            {user && user.role === 'ADMIN' && (
              <a href="/all-appointments" className="hover:opacity-60 transition-opacity">ALL APPOINTMENTS</a>
            )}
            {localStorage.getItem('emba_access') ? (
              <a href="/profile" className="hover:opacity-60 transition-opacity uppercase">{displayName}</a>
            ) : (
              <a href="/login" className="hover:opacity-60 transition-opacity">SIGN IN</a>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className={`md:hidden p-2 ${isScrolled ? 'text-gray-900' : 'text-white'}`}
            onClick={() => setMobileOpen(true)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
          </button>
        </div>
      </motion.header>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] flex justify-end">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            className="relative w-64 bg-white h-full shadow-2xl flex flex-col"
          >
            <div className="p-6 flex justify-between items-center border-b">
              <span className="text-xs tracking-[0.2em] font-bold">MENU</span>
              <button onClick={() => setMobileOpen(false)}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <nav className="flex flex-col p-6 gap-6 text-sm tracking-widest">
              <a href="/store" className="bg-black text-white px-4 py-3 font-bold text-center hover:bg-gray-800 transition-colors">SHOP</a>
              <a href="/" className="hover:text-gray-500">HOME</a>
              <a href="/bookings" className="hover:text-gray-500">BOOKINGS</a>
              {user && user.role === 'ADMIN' && (
                <a href="/all-appointments" className="hover:text-gray-500">ALL APPOINTMENTS</a>
              )}
              {localStorage.getItem('emba_access') ? (
                <a href="/profile" className="hover:text-gray-500 uppercase">{displayName}</a>
              ) : (
                <a href="/login" className="hover:text-gray-500">SIGN IN</a>
              )}
            </nav>
          </motion.aside>
        </div>
      )}
    </>
  );
}

function Hero({ image }) {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 200]);

  return (
    <section className="relative h-screen w-full overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0">
        <img
          src={image || DEFAULT_HERO_IMAGE}
          alt="Hero"
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-black/30" />
      </motion.div>

      <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white px-4">
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="text-xs md:text-sm tracking-[0.5em] mb-4 uppercase"
        >
          Est. 2023
        </motion.span>
        <motion.h1
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.7, duration: 1 }}
          className="text-5xl md:text-7xl lg:text-9xl font-serif tracking-tighter mb-6"
        >
          EMBA
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 1 }}
          className="text-sm md:text-lg tracking-[0.3em] font-light"
        >
          BY EMEFA
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="absolute bottom-10"
        >
          <div className="animate-bounce">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-white/70">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function GalleryItem({ item }) {
  // Determine grid span and aspect ratio based on type
  const isLandscape = item.type === 'landscape';
  const spanClass = isLandscape ? 'md:col-span-2' : 'md:col-span-1';
  // Portrait: 2/3 ratio (standard photo)
  // Landscape: 4/3 ratio (matches height of 2/3 when spanning 2 cols)
  const aspectClass = isLandscape ? 'aspect-[4/3]' : 'aspect-[2/3]';

  return (
    <motion.div
      className={`relative group overflow-hidden rounded-sm ${spanClass} ${aspectClass} bg-neutral-100`}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.8 }}
    >
      <img
        src={item.img}
        alt={item.title}
        className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
      />
      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors duration-500" />

      <div className="absolute inset-0 flex flex-col items-center justify-center text-center opacity-0 group-hover:opacity-100 transition-opacity duration-500 p-4">
        <span className="text-[10px] tracking-[0.3em] text-white/80 mb-2 uppercase">{item.subtitle}</span>
        <h3 className="text-2xl md:text-3xl text-white font-serif tracking-widest">{item.title}</h3>
        <a
          href={item.path_name || "/bookings"}
          className="mt-6 px-6 py-2 border border-white/50 text-white text-[10px] tracking-[0.2em] hover:bg-white hover:text-black transition-colors"
        >
          EXPLORE
        </a>
      </div>
    </motion.div>
  );
}

function Footer() {
  return (
    <footer className="bg-[#1b0d0b] text-white/80 py-16 border-t border-white/10">
      <div className="mx-auto max-w-6xl px-6">
        <div className="flex flex-col items-center justify-center gap-8">
          <div className="text-xs tracking-[0.4em] text-white/60">
            EMBA BY EMEFA
          </div>

          <div className="flex flex-col md:flex-row gap-8 md:gap-16 text-center">
            <div>
              <h4 className="text-[10px] font-bold tracking-widest text-white mb-4">CONTACT</h4>
              <ul className="space-y-2 text-[11px] tracking-wide text-white/60">
                <li>+233 542661704</li>
                <li>emefabansa@gmail.com</li>
              </ul>
            </div>
            <div>
              <h4 className="text-[10px] font-bold tracking-widest text-white mb-4">VISIT</h4>
              <ul className="space-y-2 text-[11px] tracking-wide text-white/60">
                <li>Accra, Ghana</li>
                <li>Mon – Sat, 9:00 – 18:00</li>
              </ul>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-white/5 w-full text-center">
            <p className="text-[10px] text-white/40 tracking-wider">© {new Date().getFullYear()} EMBA BY EMEFA. ALL RIGHTS RESERVED.</p>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function HomePage() {
  const [heroImage, setHeroImage] = useState(DEFAULT_HERO_IMAGE);
  const [collections, setCollections] = useState(DEFAULT_COLLECTIONS);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';
        const res = await fetch(`${baseUrl}/api/homepage/`);

        if (res.ok) {
          const data = await res.json();
          // Handle both direct array and paginated { results: [] } responses
          const items = data.results || data;

          const heroItem = items.find(d => d.section === 'HERO');
          const collectionItems = items.filter(d => d.section === 'COLLECTION');

          if (heroItem && heroItem.image_url) {
            setHeroImage(heroItem.image_url);
          }

          if (collectionItems.length > 0) {
            const formattedCollections = collectionItems.map((item) => ({
              id: item.slug || item.id,
              title: item.title,
              subtitle: item.subtitle,
              img: item.image_url,
              type: item.type || 'portrait',
              path_name: item.path_name,
            }));
            setCollections(formattedCollections);
          }
        }
      } catch (e) {
        console.error("Failed to fetch homepage content", e);
      }
    };

    fetchContent();
  }, []);

  return (
    <main className="bg-neutral-50 min-h-screen">
      <Header />
      <Hero image={heroImage} />

      <section className="py-20 w-full">
        <div className="text-center mb-16">
          <h2 className="text-sm tracking-[0.4em] text-gray-500 mb-3">COLLECTIONS</h2>
          <p className="text-3xl md:text-4xl font-serif text-gray-900">Curated Excellence</p>
        </div>

        {/* Removed auto-rows to let aspect-ratio control height */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full">
          {collections.map((item) => (
            <GalleryItem key={item.id} item={item} />
          ))}
        </div>
      </section>

      <section className="bg-[#1b0d0b] py-24 text-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-2xl mx-auto"
        >
          <h2 className="text-3xl md:text-4xl font-serif text-white mb-6">Ready to Wear?</h2>
          <p className="text-white/60 mb-10 leading-relaxed font-light">
            Experience the luxury of custom-made attire. Book a consultation with our designers to create something truly unique for your special occasion.
          </p>
          <a
            href="/bookings"
            className="inline-block border border-white/30 px-8 py-4 text-xs tracking-[0.3em] text-white hover:bg-white hover:text-[#1b0d0b] transition-all duration-300"
          >
            BOOK AN APPOINTMENT
          </a>
        </motion.div>
      </section>

      <Footer />
    </main>
  );
}